import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Sparkle, ArrowsClockwise, Clock, Palette, SignOut, User as UserIcon, Lock } from "@phosphor-icons/react";
import BrandSettings from "./BrandSettings";
import ChangePasswordDialog from "./ChangePasswordDialog";
import { toast } from "sonner";

export default function Header() {
  const { lang, setLang, t } = useI18n();
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [brandOpen, setBrandOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const navItems = user ? [
    { to: "/create", label: t("nav_create"), icon: <Sparkle weight="duotone" size={16} /> },
    { to: "/recreate", label: t("nav_recreate"), icon: <ArrowsClockwise weight="duotone" size={16} /> },
    { to: "/history", label: t("nav_history"), icon: <Clock weight="duotone" size={16} /> },
  ] : [];

  const handleLogout = async () => {
    try {
      await logout();
      toast.success(lang === "el" ? "Αποσυνδέθηκες" : "Signed out");
      nav("/login");
    } catch {
      toast.error("Error");
    }
  };

  return (
    <header className="sticky top-0 z-40 h-16 flex items-center border-b border-line bg-cream/85 backdrop-blur-xl" data-testid="app-header">
      <div className="w-full px-6 md:px-10 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
          <div className="w-8 h-8 rounded-full bg-forest flex items-center justify-center text-cream font-display text-sm">c</div>
          <div className="flex flex-col leading-none">
            <span className="font-heading text-[15px] font-semibold tracking-tight text-ink">Carousel Studio</span>
            <span className="kicker-label">EL · EN · AI</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((n) => {
            const active = loc.pathname.startsWith(n.to);
            return (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to.replace("/", "")}`}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${
                  active ? "bg-forest text-cream" : "text-ink-soft hover:text-forest hover:bg-forest/5"
                }`}>
                {n.icon}{n.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          {user && (
            <button onClick={() => setBrandOpen(true)}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full text-ink-soft hover:text-forest hover:bg-forest/5 transition"
              data-testid="header-brand" title="Brand presets">
              <Palette size={14} weight="duotone" /> Brands
            </button>
          )}

          <div className="flex items-center gap-2" data-testid="lang-switch">
            <button onClick={() => setLang("el")} data-testid="lang-el"
              className={`px-2.5 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase transition ${
                lang === "el" ? "bg-ink text-cream" : "text-ink-soft hover:text-ink"
              }`}>EL</button>
            <span className="text-line">·</span>
            <button onClick={() => setLang("en")} data-testid="lang-en"
              className={`px-2.5 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase transition ${
                lang === "en" ? "bg-ink text-cream" : "text-ink-soft hover:text-ink"
              }`}>EN</button>
          </div>

          {user ? (
            <div className="relative">
              <button onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-forest/5 transition"
                data-testid="user-menu-toggle">
                <div className="w-7 h-7 rounded-full bg-forest text-cream flex items-center justify-center text-[11px] font-semibold uppercase">
                  {(user.name || user.email).charAt(0)}
                </div>
                <span className="hidden md:block text-xs text-ink max-w-[140px] truncate">{user.email}</span>
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-line rounded-xl shadow-lg p-2 z-20" data-testid="user-menu">
                  <div className="px-3 py-2 border-b border-line mb-1">
                    <div className="text-xs text-ink-soft">{lang === "el" ? "Συνδεδεμένος ως" : "Signed in as"}</div>
                    <div className="text-sm font-medium text-ink truncate">{user.email}</div>
                    {user.role === "admin" && <div className="text-[10px] text-forest font-semibold uppercase tracking-wider mt-0.5">ADMIN</div>}
                  </div>
                  <button onClick={() => { setMenuOpen(false); setPwOpen(true); }} className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-cream-warm text-ink flex items-center gap-2" data-testid="change-password-btn">
                    <Lock size={14} weight="duotone" /> {lang === "el" ? "Αλλαγή κωδικού" : "Change password"}
                  </button>
                  <button onClick={handleLogout} className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-cream-warm text-ink flex items-center gap-2" data-testid="logout-btn">
                    <SignOut size={14} weight="duotone" /> {lang === "el" ? "Αποσύνδεση" : "Sign out"}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" data-testid="link-login"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full bg-forest text-cream hover:bg-forest-dark transition">
              <UserIcon size={14} weight="duotone" /> {lang === "el" ? "Σύνδεση" : "Sign in"}
            </Link>
          )}

          <a href="https://we2design.gr" target="_blank" rel="noopener noreferrer"
            className="hidden lg:flex items-center gap-2 pl-3 border-l border-line text-ink-soft hover:text-ink transition group"
            data-testid="maker-badge" title="Made by we2design">
            <div className="flex flex-col items-end leading-none">
              <span className="text-[9px] uppercase tracking-[0.14em] text-ink-soft/70">Made by</span>
              <span className="text-[11px] font-semibold tracking-tight text-ink">we2design</span>
            </div>
            <img src="/maker-logo.png" alt="we2design" className="w-8 h-8 rounded-md object-cover ring-1 ring-line group-hover:ring-forest/40 transition" />
          </a>
        </div>
      </div>

      <BrandSettings open={brandOpen} onOpenChange={setBrandOpen} />
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </header>
  );
}
